import React from 'react';
import CodeBlock from '@/components/CodeBlock/CodeBlock';
import { ProTip } from '../../components/LessonUI';

const PythonIfElse = () => {
    return (
        <div className="premium-course-content">
            <div className="content-hero">
                <div className="hero-badge">Control Flow</div>
                <h1>Advanced Conditional Logic</h1>
                <p className="hero-lead">
                    Move beyond simple checking. Master structural pattern matching, write elegant usage of ternary operators,
                    and learn the "Guard Clause" architectural pattern that separates junior code from senior systems.
                </p>
            </div>

            <div className="content-section chapter-intro">
                <p>
                    Conditional logic (`if`, `elif`, `else`) is the brain of your program. It dictates exactly which code runs
                    and when. While the syntax is simpleoften reading like Englishthe art lies in structuring these checks
                    to be readable, efficient, and maintainable.
                </p>
                <p>
                    Python 3.10 introduced a revolution in control flow: <strong>Structural Pattern Matching</strong> (`match`/`case`).
                    This is not just a "switch" statement; it's a way to deconstruct and analyze data structures declaratively.
                    In this deep dive, we'll cover everything from the basics of boolean evaluation to building complex
                    state machines with pattern matching.
                </p>

                <div className="learning-objectives">
                    <h3>What You'll Learn</h3>
                    <ul>
                        <li><strong>Truthiness Mechanics:</strong> How Python decides if an object is "True" or "False".</li>
                        <li><strong>Pattern Matching:</strong> Using <code>match</code>/<code>case</code> for complex data structures.</li>
                        <li><strong>The Guard Clause:</strong> Refactoring nested "Arrow" code into flat, linear logic.</li>
                        <li><strong>Ternary Operators:</strong> Writing clean one-line conditionals.</li>
                        <li><strong>Module Scripts:</strong> The <code>if __name__ == "__main__":</code> idiom explained.</li>
                    </ul>
                </div>
            </div>

            <div className="content-section">
                <h2>The Foundation: Truthy and Falsy</h2>
                <p>
                    In Python, every object has an intrinsic boolean value. This "truthiness" allows you to write clean, expressive conditionals
                    without explicit comparison operators. When you write <code>if my_list:</code>, you are asking: "Is this list not empty?"
                </p>

                <h3>1. What is False?</h3>
                <p>
                    The following values are considered <strong>False</strong>. Everything else is <strong>True</strong>.
                </p>
                <ul>
                    <li><strong>Constants:</strong> <code>None</code> and <code>False</code>.</li>
                    <li><strong>Zero of any numeric type:</strong> <code>0</code>, <code>0.0</code>, <code>0j</code>, <code>Decimal(0)</code>, <code>Fraction(0, 1)</code>.</li>
                    <li><strong>Empty sequences and collections:</strong> <code>""</code> (empty string), <code>()</code> (tuple), <code>[]</code> (list), <code>{'{'}{'}'}</code> (dictionary), <code>set()</code>, <code>range(0)</code>.</li>
                </ul>

                <CodeBlock language="python" code={`# The "Pythonic" check
name = ""
if not name:
    print("Name is empty")

# The "Non-Pythonic" check (Avoid this!)
if name == "":
    print("Name is empty")`} />

                <h3>2. Under the Hood: `__bool__` and `__len__`</h3>
                <p>
                    Python determines truthiness using a specific protocol. When you use an object in a boolean context (like an <code>if</code> statement
                    or the <code>bool()</code> constructor), Python follows these steps:
                </p>
                <ol>
                    <li>
                        <strong>Check for `__bool__`:</strong> If the object has a <code>__bool__()</code> method, Python calls it.
                        It must return <code>True</code> or <code>False</code>.
                    </li>
                    <li>
                        <strong>Check for `__len__`:</strong> If `__bool__` is missing, Python looks for `__len__()`.
                        If it returns <code>0</code>, the object is False. Otherwise, it is True.
                    </li>
                    <li>
                        <strong>Default:</strong> If neither method exists, the object is considered <strong>True</strong>.
                    </li>
                </ol>

                <div className="code-example">
                    <div className="code-header">
                        <span>Customizing Truthiness</span>
                    </div>
                    <CodeBlock language="python" code={`class ShoppingCart:
    def __init__(self):
        self.items = []

    def add(self, item):
        self.items.append(item)

    # Magic method: Controlled by length
    def __len__(self):
        return len(self.items)

class User:
    def __init__(self, username):
        self.username = username
        self.is_active = False

    # Magic method: Explicit boolean value
    def __bool__(self):
        # A User is "True" only if they are active
        return self.is_active

cart = ShoppingCart()
user = User("Alice")

print(bool(cart)) # False (len is 0)
print(bool(user)) # False (is_active is False)

cart.add("Apple")
user.is_active = True

print(bool(cart)) # True
print(bool(user)) # True`} />
                </div>


                <h3>History Lesson: When True wasn't True</h3>
                <p>
                    It might shock you, but in Python 2.x, <code>True</code> and <code>False</code> were not keywords!
                    They were built-in global variables. This meant you could do evil things like:
                </p>
                <CodeBlock language="python" code={`# Python 2.7 Nightmare
True = False
if True:
    print("This will never print!")`} />
                <p>
                    This is why some old-school Python programmers habitually check <code>if x:</code> instead of <code>if x == True</code>.
                    While Python 3 fixed this (making them reserved keywords), the idiom of implicit truthiness remains the "Gold Standard" way to write code.
                </p>
            </div>

            <div className="content-section">
                <h2>The Ternary Operator (Conditional Expressions)</h2>
                <p>
                    Sometimes you want to assign a value based on a condition.
                    Instead of 4 lines of `if/else`, Python offers a one-liner. This is technically called a "Conditional Expression".
                </p>

                <h3>1. Syntax and Basic Usage</h3>
                <div className="code-example">
                    <div className="code-header">
                        <span>Short & Sweet Assignment</span>
                    </div>
                    <CodeBlock language="python" code={`# Verbose Way 
if age >= 18:
    status = "Adult"
else:
    status = "Minor"

# Pythonic Way 
status = "Adult" if age >= 18 else "Minor"

# The logic: [Value If True] if [Condition] else [Value If False]`} />
                </div>

                <h3>2. Where to Use It</h3>
                <p>
                    Ternary operators are best used for simple value assignment. They shine in:
                </p>
                <ul>
                    <li><strong>Return statements:</strong> <code>return "Yes" if is_valid else "No"</code></li>
                    <li><strong>List Comprehensions:</strong> <code>[x if x {">"} 0 else 0 for x in data]</code></li>
                    <li><strong>Lambda Functions:</strong> <code>lambda x: "Even" if x % 2 == 0 else "Odd"</code></li>
                </ul>

                <h3>3. A History Lesson: The Tuple Hack</h3>
                <p>
                    Before Python 2.5, the ternary operator didn't exist! Hackers used a tuple indexing trick which is dangerous and should be avoided today.
                </p>
                <CodeBlock language="python" code={`# The Ancient Hack (Don't use this!)
# (Value_If_False, Value_If_True)[Condition]
status = ("Minor", "Adult")[age >= 18]

#  DANGER: Both sides are evaluated!
# If one side raises an error or has side effects, IT WILL RUN even if not selected.
# result = (1/0, 10)[True] # ZeroDivisionError!`} />

                <ProTip><div className="info-icon"></div>
                    <div className="info-text">
                        <strong>Avoid Nesting:</strong> While you <em>can</em> nest ternary operators, it makes code unreadable.
                        <code>x = "A" if c1 else "B" if c2 else "C"</code> is a sign you should switch to a real <code>if/elif</code> block.
                    </div></ProTip>
            </div>

            <div className="content-section">
                <h2>Legacy Patterns: The Dictionary Switch</h2>
                <p>
                    Before Python 3.10 brought us the <code>match</code> statement, Python developers used a clever trick to emulate switch-case logic: the Dictionary Map.
                    You will encounter this pattern frequently in older codebases (and it is still useful for simple mapping!).
                </p>
                <p>
                    The core idea is simple: instead of writing a chain of <code>if/elif/elif</code>, you define a dictionary where keys are the conditions
                    and values are the results (or functions to call).
                </p>

                <div className="code-example">
                    <div className="code-header">
                        <span>The Old Way (Pre-3.10)</span>
                    </div>
                    <CodeBlock language="python" code={`def get_database(name):
    # Dict mapping replaces if/elif chain
    databases = {
        "mysql": "Connecting to MySQL...",
        "postgres": "Connecting to PostgreSQL...",
        "sqlite": "Connecting to SQLite...",
    }
    
    # Use .get() with a default value for the "else" case
    return databases.get(name, "Unknown Database")

# Pros: O(1) lookup time (faster than long if-chains)
# Cons: Values are eagerly evaluated (unless you use lambdas)`} />
                </div>

                <h3>The "Lambda" Optimization</h3>
                <p>
                    One major flaw of the dictionary approach is "Eager Evaluation". If the values in your dictionary are function calls,
                    Python runs <em>all of them</em> when defining the dictionary, which is disastrous.
                    The workaround was to store functions (or lambdas) and call them <em>after</em> lookup.
                </p>
            </div>

            <div className="content-section">
                <h2>Deep Dive: Short-Circuit Evaluation</h2>
                <p>
                    Boolean operators <code>and</code> & <code>or</code> are lazily evaluated in Python.
                    They don't just return <code>True</code> or <code>False</code>; they return the <strong>last evaluated operand</strong>.
                    This behavior allows for powerful, concise idioms (often found in JavaScript/Ruby too).
                </p>

                <h3>1. The "Default Value" Idiom (OR)</h3>
                <p>
                    The <code>or</code> operator searches for the first Truthy value. If it finds one, it stops and returns it.
                    If not, it returns the last value.
                </p>
                <CodeBlock language="python" code={`# Common Pattern: providing defaults
username = input_name or "Anonymous"

# Usage in Dicts
config = settings.get("db_url") or "localhost:5432"`} />

                <h3>2. The "Safety Check" Idiom (AND)</h3>
                <p>
                    The <code>and</code> operator stops at the first Falsy value. This is perfect for checking existence before access.
                </p>
                <CodeBlock language="python" code={`user = get_user()

# Only calls .is_active() if user is not None
# Returns None immediately if user is None
is_ready = user and user.is_active()

# Equivalent to:
# if user:
#     is_ready = user.is_active()
# else:
#     is_ready = None`} />

                <ProTip><div className="info-icon"></div>
                    <div className="info-text">
                        <strong>Performance Win:</strong> Short-circuiting isn't just syntactic sugar; it is a performance optimization.
                        If you have <code>if heavy_calc() and light_calc():</code>, you are doing it wrong!
                        Always put the cheap/likely-to-fail check first: <code>if light_check() and heavy_check():</code>.
                    </div></ProTip>
            </div>

            <div className="content-section">
                <h2>Structural Pattern Matching (Python 3.10+)</h2>
                <p>
                    Python 3.10 introduced the <code>match</code> statement. It looks like a specialized "switch" case from C or Java,
                    but it's actually a powerful <strong>structural pattern matching</strong> tool (similar to Scala or Rust).
                </p>

                <div className="code-example">
                    <div className="code-header">
                        <span>The Modern "Switch"</span>
                    </div>
                    <CodeBlock language="python" code={`http_status = 404

match http_status:
    case 200:
        print("Success")
    case 400:
        print("Bad Request")
    case 404:
        print("Not Found")
    case 500 | 501 | 502:  # Match multiple values (OR)
        print("Server Error")
    case _:
        print("Unknown Status")  # The default case`} />
                </div>

                <h3>Matching Data Structures</h3>
                <p>
                    This is where <code>match</code> shines. You can match against the <em>shape</em> of data.
                </p>

                <div className="code-example">
                    <div className="code-header">
                        <span>Unpacking & Matching Simultaneously</span>
                    </div>
                    <CodeBlock language="python" code={`# Imagine parsing a command from a UI
command = ["move", 10, 20]

match command:
    case ["quit"]:
        print("Quitting...")
    case ["move", x, y]:
        # Matches a list with exactly 3 items where 1st is "move"
        # AND captures the 2nd and 3rd items into x and y!
        print(f"Moving to {x}, {y}")
    case ["shoot", *targets]:
        # Matches "shoot" and captures ALL remaining items
        print(f"Shooting at {targets}")
    case _:
        print("Invalid Command")`} />
                </div>

                <h3>Advanced Matching Patterns</h3>
                <p>
                    Pattern matching isn't limited to lists. You can match against dictionaries (JSON data), class instances,
                    and even add extra logic with "Guards".
                </p>

                <h4>1. Matching Dictionaries (JSON)</h4>
                <p>
                    When matching dictionaries, you check if the <em>keys</em> exist and match their values.
                    Extra keys are ignored, unlike sequence matching where the length must match.
                </p>
                <CodeBlock language="python" code={`event = {"type": "click", "x": 100, "y": 200, "meta": "ignored"}

match event:
    case {"type": "click", "x": x, "y": y}:
        print(f"User clicked at {x}, {y}")
    case {"type": "keypress", "key": "Enter"}:
        print("User pressed Enter")
    case {"type": "keypress"}:
        print("User pressed some other key")`} />

                <h4>2. Matching Class Instances</h4>
                <p>
                    You can match against custom objects. This replaces complex `isinstance()` checks.
                </p>
                <CodeBlock language="python" code={`class Button:
    def __init__(self, color):
        self.color = color

class TextField:
    def __init__(self, text):
        self.text = text

ui_element = Button("red")

match ui_element:
    case Button(color="red"):
        print("Stop button pressed!")
    case Button(color=c):
        print(f"Button of color {c} pressed")
    case TextField(text=t):
        print(f"Processing text: {t}")`} />

                <h4>3. Guard Clauses in Cases</h4>
                <p>
                    Sometimes the pattern matches, but you need an extra check. Use `if` <em>inside</em> the case.
                </p>
                <CodeBlock language="python" code={`number = 15

match number:
    case x if x % 2 == 0:
        print(f"{x} is even")
    case x if x % 2 != 0:
        print(f"{x} is odd")`} />

                <ProTip><div className="info-icon"></div>
                    <div className="info-text">
                        <strong>Why is this revolutionary?</strong> Before Python 3.10, implementing this logic required
                        nested <code>isinstance()</code> checks, attribute lookups, and manual length checks using <code>len()</code>.
                        Pattern matching collapses all that accidental complexity.
                    </div></ProTip>
            </div>

            <div className="content-section">
                <h2>Architectural Pattern: The Guard Clause</h2>
                <p>
                    Beginners often write "Arrow Code" - nested `if` statements that point to the right like an arrow.
                    Professionals use <strong>Guard Clauses</strong>: handle edge cases first and return early.
                </p>

                <div className="code-example">
                    <div className="code-header">
                        <span>Refactoring Arrow Code</span>
                    </div>
                    <CodeBlock language="python" code={`#  The "Arrow" Anti-Pattern
def process_payment(user, amount):
    if user.is_active:
        if amount &gt; 0:
            if user.has_funds(amount):
                user.pay(amount)
                return "Success"
            else:
                return "Insufficient Funds"
        else:
            return "Invalid Amount"
    else:
        return "User Inactive"

# The Guard Clause Pattern (Flat & Clean)
def process_payment(user, amount):
    # The "Bouncer" checks: Stop bad data at the door
    if not user.is_active:
        return "User Inactive"
    
    if amount <= 0:
        return "Invalid Amount"
        
    if not user.has_funds(amount):
        return "Insufficient Funds"

    # Happy path is at the main indentation level!
    user.pay(amount)
    return "Success"`} />
                </div>

                <h3>Step-by-Step Refactoring Guide</h3>
                <p>
                    Let's break down exactly how we transformed the code above. This is a mental framework you can apply to any function.
                </p>
                <ol>
                    <li>
                        <strong>Identify the "Happy Path":</strong> What is the core purpose of the function?
                        In <code>process_payment</code>, the goal is <code>user.pay(amount)</code>.
                        Ideally, this line should be at the very end, indented as little as possible.
                    </li>
                    <li>
                        <strong>Invert the Checks:</strong> Look at your first <code>if</code>: <code>if user.is_active:</code>.
                        Ask: "What happens if not?". The answer is "Return Error".
                        Flip it: <code>if not user.is_active: return "Error"</code>.
                    </li>
                    <li>
                        <strong>Un-nest:</strong> Once you return early, you don't need an <code>else</code> block.
                        The rest of the function <em>is</em> the else block. Delete the indentation.
                    </li>
                    <li>
                        <strong>Repeat:</strong> Do this for every condition until only the Happy Path remains.
                    </li>
                </ol>

                <ProTip><div className="info-icon"></div>
                    <div className="info-text">
                        <strong>Mental Model:</strong> Think of your function as a funnel.
                        At the top (start), anything can enter.
                        Your guard clauses are filters.
                        By the time code reaches the bottom, you <em>know</em> the data is valid.
                        You don't need to check again.
                    </div></ProTip>

                <h3>Loop Guards (The `continue` Statement)</h3>
                <p>
                    Guard clauses aren't just for functions; they are amazing for loops. instead of nesting your loop logic,
                    skip the bad iterations early.
                </p>
                <CodeBlock language="python" code={`#  Nested Loop Logic
for user in users:
    if user.is_active:
        if user.has_subscription:
            send_email(user)

# Flat Loop Logic
for user in users:
    if not user.is_active:
        continue
        
    if not user.has_subscription:
        continue
        
    # Main logic
    send_email(user)`} />
            </div>

            <div className="content-section">
                <h2>The `if __name__ == "__main__":` Idiom</h2>
                <p>
                    You will see this in almost every professional Python script. It controls what runs when a file is executed.
                    But how does it strictly work?
                </p>
                <p>
                    Every Python module has a special built-in variable called <code>__name__</code>.
                </p>
                <ul>
                    <li>If you run <code>python script.py</code>, Python sets <code>__name__</code> to the string <code>"__main__"</code>.</li>
                    <li>If you import the file (<code>import script</code>), Python sets <code>__name__</code> to the filename <code>"script"</code>.</li>
                </ul>

                <h3>The "Library vs Script" Duality</h3>
                <p>
                    This idiom allows a single file to behave in two different ways: as a command-line script (doing something)
                    or as a library of functions (providing tools). Without this guard, importing your library would accidentally execute its code!
                </p>

                <div className="code-example">
                    <div className="code-header">
                        <span>Script Boilerplate</span>
                    </div>
                    <CodeBlock language="python" code={`# my_script.py

def useful_function():
    return "I am useful"

def main():
    # Only runs when script is executed directly
    print("Executing script...")
    result = useful_function()
    print(result)

# The Guard
if __name__ == "__main__":
    main()`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Troubleshooting Common Errors</h2>
                <div className="comparison-table">
                    <table>
                        <thead>
                            <tr>
                                <th>Error</th>
                                <th>Common Cause</th>
                                <th>Fix</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td><code>SyntaxError: invalid syntax</code></td>
                                <td>Missing colon (<code>:</code>) after <code>if</code>/<code>else</code></td>
                                <td>Add the colon!</td>
                            </tr>
                            <tr>
                                <td><code>IndentationError</code></td>
                                <td>Mixing tabs and spaces</td>
                                <td>Use 4 spaces consistently.</td>
                            </tr>
                            <tr>
                                <td><code>SyntaxError: cannot assign to literal</code></td>
                                <td>Using <code>=</code> instead of <code>==</code> in condition</td>
                                <td>Change <code>if x = 5</code> to <code>if x == 5</code></td>
                            </tr>
                            <tr>
                                <td><code>UnboundLocalError</code></td>
                                <td>Using variable defined only in <code>if</code> block</td>
                                <td>Initialize variable before the <code>if</code> statement.</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>

            <div className="content-section">
                <h2>The Cost of Branching: Cyclomatic Complexity</h2>
                <p>
                    It's easy to add "just one more if-statement," but every branch adds to your code's <strong>Cyclomatic Complexity</strong>.
                    This is a software metric that measures the number of linearly independent paths through a program's source code.
                </p>
                <p>
                    Why does this matter?
                </p>
                <ul>
                    <li><strong>Testing Difficulty:</strong> To test a function fully, you must execute every possible path. Calculate complexity = minimum number of tests needed.</li>
                    <li><strong>Cognitive Load:</strong> Humans can only hold 7 (2) items in working memory. Deeply nested logic exceeds this limit rapidly.</li>
                </ul>

                <h3>Strategies to Reduce Complexity</h3>
                <ol>
                    <li><strong>Flatten Logic:</strong> Use Guard Clauses (as seen above) to return early.</li>
                    <li><strong>Use Data Structures:</strong> Swap long <code>if/elif</code> chains for Dictionary Maps.</li>
                    <li><strong>Polymorphism:</strong> If you are switching on type, use Class Inheritance and method overriding instead.</li>
                </ol>

                <div className="code-example">
                    <div className="code-header">
                        <span>Polymorphism vs Branching</span>
                    </div>
                    <CodeBlock language="python" code={`#  High Complexity (Branching on Type)
def speak(animal):
    if isinstance(animal, Dog):
        return "Woof"
    elif isinstance(animal, Cat):
        return "Meow"
    elif isinstance(animal, Cow):
        return "Moo"

# Low Complexity (Polymorphism)
# The logic is distributed, not central.
class Dog:
    def speak(self): return "Woof"

class Cat:
    def speak(self): return "Meow"

def speak(animal):
    return animal.speak()`} />
                </div>
            </div>

            <div className="content-section common-pitfalls">
                <h2>Common Pitfalls</h2>

                <div className="pitfall-card">
                    <h3> Checking Booleans Explicitly</h3>
                    <p><strong>Why it's wrong:</strong> <code>if x == True:</code> performs a value comparison. It's redundant and non-Pythonic.
                        Worse, <code>if x == False:</code> is harder to read than <code>if not x:</code>.</p>
                    <CodeBlock language="python" code={`#  Bad
if is_valid == True: ...

# Good
if is_valid: ...`} />
                </div>

                <div className="pitfall-card">
                    <h3> Complex One-Liners</h3>
                    <p><strong>Why it's wrong:</strong> Code is read more often than it is written. Saving 3 lines isn't worth 30 minutes of debugging later.</p>
                    <CodeBlock language="python" code={`#  Reads like a riddle
x = 'A' if y &gt; 10 else 'B' if y &gt; 5 else 'C'

# Clearer
if y &gt; 10:
    x = 'A'
elif y &gt; 5:
    x = 'B'
else:
    x = 'C'`} />
                </div>

                <div className="pitfall-card">
                    <h3> Yoda Conditions</h3>
                    <p><strong>Why it's wrong:</strong> Writing <code>if 10 == x:</code> is common in C/Java to avoid assignment errors.
                        In Python, assignment in <code>if</code> raises a SyntaxError anyway, so write natural English: <code>if x == 10:</code>.</p>
                </div>

                <div className="pitfall-card">
                    <h3> Not Using Range Chaining</h3>
                    <p><strong>Why it's wrong:</strong> Python allows <code>lo &lt; x &lt; hi</code>. Don't write unnecessary <code>and</code> operators.</p>
                    <CodeBlock language="python" code={`#  Verbose
if x >= 10 and x <= 20: ...

# Pythonic
if 10 <= x <= 20: ...`} />
                </div>
            </div>
        </div >
    );
};

export default PythonIfElse;


